import { Dayjs } from 'dayjs';
import {
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { InvoiceProvider } from './invoice-provider.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { Option } from '../../../utility/option.js';
import { VersionRange } from '../../core/version-range.js';
import { InvoiceBase } from '../../../contracts/invoice.js';
import { isSameList } from '../../dto/data-model-utils.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { LegacyReceipt } from './legacy-receipt.js';
import { OfflineReceiptType } from './offline-receipt-type.js';

export class OfflineReceipt extends InvoiceBase {
  static readonly TYPE_ID: string = '18f98ad3858a4f1ab4e89ff50a664a8e';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly name: string,
    public readonly provider: InvoiceProvider,
    public readonly downloadUrl: string,
    public readonly type: OfflineReceiptType,
    public readonly creationDate: Dayjs,

    // Only used for backwards compatible serialization.
    // Will be removed once legacy receipts are deprecated.
    private readonly isElectronic: boolean | null,
    private readonly emailAddresses: string[] | null,
  ) {
    super();
  }

  get $typeId(): string {
    return OfflineReceipt.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OfflineReceipt.SCHEMA_VERSION);
  }

  get sanitizedName(): string | undefined {
    let name = this.name;

    if(!name) {
      return name;
    }

    name = name.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    return name.replace(/[^a-zA-Z0-9]/g, '_');
  }

  static create(args: {
    uniqueId: string,
    name: string,
    provider: InvoiceProvider,
    downloadUrl: string,
    type: OfflineReceiptType,
    creationDate: Dayjs,
    isElectronic: boolean | null,
    emailAddresses: string[] | null,
  }): OfflineReceipt {
    return new OfflineReceipt(
        args.uniqueId,
        args.name,
        args.provider,
        args.downloadUrl,
        args.type,
        args.creationDate,
        args.isElectronic,
        args.emailAddresses,
    );
  }

  static fromLegacyAsActive(legacyReceipt: LegacyReceipt): OfflineReceipt {
    return OfflineReceipt.create({
      uniqueId: legacyReceipt.uniqueId,
      name: legacyReceipt.name,
      provider: legacyReceipt.provider,
      downloadUrl: legacyReceipt.downloadUrl,
      type: OfflineReceiptType.Receipt,
      creationDate: TimeZone.cloneDate(legacyReceipt.creationDate),
      isElectronic: legacyReceipt.isElectronic,
      emailAddresses: [...legacyReceipt.emailAddresses],
    });
  }

  static fromLegacyAsCancelled(legacyReceipt: LegacyReceipt): OfflineReceipt | null {
    if(!legacyReceipt.isCancelled || legacyReceipt.cancellationDate == null) {
      return null;
    }

    // older receipts don't track cancelled invoice url so we fallback to the original one, best we can do
    const downloadUrl = legacyReceipt.cancelledDownloadUrl ?? legacyReceipt.downloadUrl;
    const creationDate = legacyReceipt.cancellationDate;

    return OfflineReceipt.create({
      uniqueId: legacyReceipt.uniqueId,
      name: legacyReceipt.name,
      provider: legacyReceipt.provider,
      downloadUrl: downloadUrl,
      type: OfflineReceiptType.Receipt,
      creationDate: TimeZone.cloneDate(creationDate),
      isElectronic: legacyReceipt.isElectronic,
      emailAddresses: [...legacyReceipt.emailAddresses],
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineReceipt {
    return OfflineReceipt.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      name: MapUtils.getString(map, 'name'),
      provider: toEnumFromString(MapUtils.getString(map, 'provider'), InvoiceProvider),
      downloadUrl: MapUtils.getString(map, 'downloadUrl'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      type: toEnumFromString(MapUtils.getString(map, 'type'), OfflineReceiptType),
      isElectronic: MapUtils.getBooleanOrNull(map, 'isElectronic'),
      emailAddresses: MapUtils.getListOrNull(map, 'emailAddresses', (x) => JSONUtils.decodeString(x)),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': this.name,
      'provider': fromEnumAsString(this.provider),
      'downloadUrl': this.downloadUrl,
      'creationDate': context.encodeDate(this.creationDate),
      'type': fromEnumAsString(this.type),
      'isElectronic': this.isElectronic,
      'emailAddresses': this.emailAddresses,
    };
  }

  clone(): this {
    return OfflineReceipt.create({
      uniqueId: this.uniqueId,
      name: this.name,
      provider: this.provider,
      downloadUrl: this.downloadUrl,
      creationDate: TimeZone.cloneDate(this.creationDate),
      type: this.type,
      isElectronic: this.isElectronic,
      emailAddresses: this.emailAddresses != null ? [...this.emailAddresses] : null,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.name === this.name &&
        other.provider === this.provider &&
        other.downloadUrl === this.downloadUrl &&
        TimeZone.isSameInstant(this.creationDate, other.creationDate) &&
        other.isElectronic === this.isElectronic &&
        isSameList(other.emailAddresses, this.emailAddresses) &&
        other.type === this.type;
  }

  copyWith(args: {
    uniqueId: Option<string>,
    name: Option<string>,
    provider: Option<InvoiceProvider>,
    downloadUrl: Option<string>,
    creationDate: Option<Dayjs>,
    type: Option<OfflineReceiptType>,
  }): OfflineReceipt {
    return OfflineReceipt.create({
      uniqueId: args.uniqueId.getOrElse(this.uniqueId),
      name: args.name.getOrElse(this.name),
      provider: args.provider.getOrElse(this.provider),
      downloadUrl: args.downloadUrl.getOrElse(this.downloadUrl),
      creationDate: args.creationDate.getOrElse(TimeZone.cloneDate(this.creationDate)),
      type: args.type.getOrElse(this.type),
      isElectronic: this.isElectronic,
      emailAddresses: this.emailAddresses != null ? [...this.emailAddresses] : null,
    });
  }

  toDiagnostics(): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': this.name,
      'provider': fromEnumAsString(this.provider),
      'downloadUrl': this.downloadUrl,
      'creationDate': this.creationDate.toISOString(),
      'type': fromEnumAsString(this.type),
      'isElectronic': this.isElectronic,
      'emailAddresses': this.emailAddresses,
    };
  }
}

export class OfflineReceiptBuilder extends DataModelBuilder<OfflineReceipt> {
  build(context: SerializationContext, map: SerializedData): OfflineReceipt {
    return OfflineReceipt.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OfflineReceipt.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflineReceipt.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
